import { Button } from 'antd'
import React from 'react'
import styled from 'styled-components/macro'
import {
  isDashboardAppOnlyCtaAction,
  STANDING_ORDERS_DASHBOARD_HONEST_NOTE,
} from 'src/utils/growthEngineCtaRoutes'
import {
  getStockDashboardHonestNote,
  rewriteFreeStockDashboardCopy,
  shouldBlockStockStartCta,
  shouldShowStockReportsHonestNote,
} from 'src/utils/growthEngineStockHonestFirstTry'
import { formatGuidedEmptyMinutes } from 'src/utils/growthEngineRecommendation'
import GrowthEngineHumanDemoRow from 'src/components/growthEngine/GrowthEngineHumanDemoRow'

/** Shared Phase 1 secondary actions — clearer than generic “later / not now”. */
export const GROWTH_ENGINE_SNOOZE_LABEL = 'Remind me in a week'
export const GROWTH_ENGINE_DISMISS_LABEL = 'Not interested'

/**
 * Reusable Growth Engine nudge surface (inline card — never a fullscreen popup).
 *
 * Expects Express recommendation fields: title, body, cta, optional progress.
 * Snooze / dismiss labels are owned by this shared card so every nudge stays consistent.
 * Parent owns fetch / cool-off posting.
 */
const GrowthEngineNudgeCard = ({
  title,
  body,
  featureName,
  featureId,
  progress,
  ctaLabel,
  honestEmptyNote,
  estimatedMinutes,
  snoozeLabel = GROWTH_ENGINE_SNOOZE_LABEL,
  dismissLabel = GROWTH_ENGINE_DISMISS_LABEL,
  onCta,
  onSnooze,
  onDismiss,
  busy = false,
  showHumanPath = true,
  footerExtra = null,
  className,
  'data-testid': dataTestId = 'growth-engine-nudge-card',
}) => {
  const resolvedCtaLabel = ctaLabel || 'Continue'
  const hasActions = Boolean(onCta || onSnooze || onDismiss)
  const minutesLabel = formatGuidedEmptyMinutes(estimatedMinutes)

  if (!title && !body) {
    return null
  }

  return (
    <Card
      className={className}
      data-testid={dataTestId}
      role='region'
      aria-label={title || 'Recommendation'}
    >
      <Copy>
        <TitleRow>
          {title ? <Title data-testid='nudge-title'>{title}</Title> : null}
          {featureName ? (
            <FeatureName data-testid='nudge-feature-name'>
              {featureName}
            </FeatureName>
          ) : null}
          {progress ? (
            <Progress data-testid='nudge-progress'>{progress}</Progress>
          ) : null}
        </TitleRow>
        {body ? <Body data-testid='nudge-body'>{body}</Body> : null}
        {minutesLabel ? (
          <Minutes data-testid='nudge-minutes'>{minutesLabel}</Minutes>
        ) : null}
        {honestEmptyNote ? (
          <HonestNote data-testid='nudge-honest-empty'>
            {honestEmptyNote}
          </HonestNote>
        ) : null}
      </Copy>

      <Footer>
        {hasActions ? (
          <Actions>
            {onCta && ctaLabel ? (
              <Button
                type='primary'
                onClick={onCta}
                loading={busy}
                disabled={busy}
                data-testid='nudge-cta'
              >
                {resolvedCtaLabel}
              </Button>
            ) : null}
            {onSnooze ? (
              <SecondaryLink
                type='link'
                onClick={onSnooze}
                disabled={busy}
                data-testid='nudge-snooze'
              >
                {snoozeLabel}
              </SecondaryLink>
            ) : null}
            {onDismiss ? (
              <SecondaryLink
                type='link'
                onClick={onDismiss}
                disabled={busy}
                data-testid='nudge-dismiss'
              >
                {dismissLabel}
              </SecondaryLink>
            ) : null}
          </Actions>
        ) : null}
        {footerExtra}
        {showHumanPath ? (
          <GrowthEngineHumanDemoRow
            compact
            featureId={featureId}
            featureLabel={featureName}
          />
        ) : null}
      </Footer>
    </Card>
  )
}

/**
 * Map Express recommendation (+ optional Guided Success) onto card props.
 * Business-case title/body/CTA come from the API. Feature name is secondary.
 * Handlers stay in the parent.
 *
 * @param {object|null} recommendation
 * @param {{ guidedSuccess?: object|null, currentPlan?: string|null }} [options]
 */
export function getNudgeCardPropsFromRecommendation(
  recommendation,
  options = {},
) {
  const guidedSuccess = options.guidedSuccess || null
  const businessCase = guidedSuccess?.businessCase
  const emptyState = guidedSuccess?.emptyState
  const title =
    businessCase?.title || recommendation?.title || emptyState?.title || ''
  const body =
    businessCase?.body || recommendation?.body || emptyState?.body || ''
  if (!recommendation && !title && !body) return null

  const cta = recommendation?.cta || emptyState?.cta || null
  const featureName =
    recommendation?.featureName || guidedSuccess?.featureName || null
  const featureId = recommendation?.featureId || guidedSuccess?.featureId
  const appOnlyCta = isDashboardAppOnlyCtaAction(cta?.action)
  const continueOnApp = recommendation?.continueOn === 'app'
  const stockStartBlocked = shouldBlockStockStartCta({
    action: cta?.action,
    featureId,
    plan: options.currentPlan,
  })
  const hideCta = appOnlyCta || stockStartBlocked || continueOnApp
  const stockReportsNote =
    !hideCta &&
    shouldShowStockReportsHonestNote(options.currentPlan) &&
    (featureId === 'stock_takes' || cta?.action === 'start_stock_take')
      ? getStockDashboardHonestNote(options.currentPlan)
      : null
  const honestEmptyNote = appOnlyCta
    ? STANDING_ORDERS_DASHBOARD_HONEST_NOTE
    : stockStartBlocked
    ? getStockDashboardHonestNote(options.currentPlan)
    : continueOnApp
    ? recommendation?.honestNote || STANDING_ORDERS_DASHBOARD_HONEST_NOTE
    : stockReportsNote

  const displayTitle = stockStartBlocked
    ? rewriteFreeStockDashboardCopy(title)
    : title
  const displayBody = stockStartBlocked
    ? rewriteFreeStockDashboardCopy(body)
    : body

  return {
    title: displayTitle,
    body: displayBody,
    featureName,
    progress: recommendation?.progress || null,
    ctaLabel: hideCta ? null : cta?.label || 'Continue',
    honestEmptyNote,
    // Shared UX labels — do not vary by Express intent (cool-offs are the same rules).
    snoozeLabel: GROWTH_ENGINE_SNOOZE_LABEL,
    dismissLabel: GROWTH_ENGINE_DISMISS_LABEL,
    intentId: recommendation?.intentId,
    featureId,
    ctaAction: hideCta ? null : cta?.action || null,
    variantIndex: recommendation?.variantIndex,
    estimatedMinutes:
      recommendation?.estimatedMinutes ??
      emptyState?.estimatedMinutes ??
      businessCase?.estimatedMinutes ??
      null,
  }
}

const Card = styled.div`
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  border: 1px solid ${({ theme }) => theme.colours.borderGrey || '#e8e8e8'};
  border-left: 3px solid
    ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 1100px;
  padding: 12px 16px;
  width: 100%;
  box-sizing: border-box;
`

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TitleRow = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
`

const Title = styled.h3`
  color: ${({ theme }) => theme.colours.bbqBlack || '#262626'};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
`

const FeatureName = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey || '#8c8c8c'};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.3;
  margin: 0;
`

const Body = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey || '#595959'};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`

const Minutes = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey || '#8c8c8c'};
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
`

const Progress = styled.p`
  color: ${({ theme }) => theme.colours.seaweedGreen || '#389e0d'};
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
`

const HonestNote = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey || '#8c8c8c'};
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  margin: 4px 0 0;
`

const Footer = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  justify-content: space-between;
`

const Actions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 0;
`

const SecondaryLink = styled(Button)`
  && {
    color: ${({ theme }) => theme.colours.oliveGrey || '#8c8c8c'};
    height: auto;
    padding: 0 8px;
  }

  &&:hover,
  &&:focus {
    color: ${({ theme }) => theme.colours.bbqBlack || '#595959'};
  }
`

export default GrowthEngineNudgeCard
